import { forwardRef } from 'react';
import type { ButtonProps as AriakitButtonProps } from '@ariakit/react';
import { Button as AriakitButton } from '@ariakit/react';
import type { ClassValue } from 'clsx';
import clsx from 'clsx';

import { Box } from '../Box/Box';
import type { IconProps } from '../icons';
import type { Props as TextProps } from '../Text/Text';
import { Text } from '../Text/Text';

import * as styles from './styles.css';

export type ButtonProps = React.PropsWithChildren &
  AriakitButtonProps &
  styles.Variants & {
    iconLeft?: React.ReactElement<IconProps>;
    iconRight?: React.ReactElement<IconProps>;
    onPress?: () => void;
    cssClass?: ClassValue | ClassValue[];
    trackingId?: string;
  };

const buttonToTextSize = {
  xSmall: 'xSmall',
  small: 'small',
  medium: 'small',
  large: 'small',
  xLarge: 'large',
} as const;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      iconLeft,
      iconRight,
      size = styles.defaultSize,
      kind,
      emphasis,
      className,
      cssClass,
      disabled,
      trackingId,
      ...buttonProps
    },
    ref,
  ) => (
    <AriakitButton
      disabled={disabled}
      className={clsx(
        styles.variants({
          kind,
          size,
          emphasis,
        }),
        className,
        cssClass,
      )}
      ref={ref}
      type="button"
      {...buttonProps}
    >
      <ButtonContent
        iconLeft={iconLeft}
        iconRight={iconRight}
        size={size}
        kind={kind}
        emphasis={emphasis}
        disabled={disabled}
      >
        {children}
      </ButtonContent>
    </AriakitButton>
  ),
);

type ButtonContentProps = Pick<
  ButtonProps,
  'children' | 'disabled' | 'emphasis' | 'iconLeft' | 'iconRight' | 'kind' | 'size'
>;
export const ButtonContent: React.FC<ButtonContentProps> = ({
  children,
  disabled,
  emphasis,
  iconLeft,
  iconRight,
  kind,
  size = styles.defaultSize,
}) => {
  const textSize: TextProps['size'] = buttonToTextSize[size];

  return (
    <>
      {iconLeft && (
        <Box
          as="span"
          display="inline-flex"
          disabled={disabled}
          cssClass={styles.iconVariants({
            size,
            emphasis,
            kind,
          })}
        >
          {iconLeft}
        </Box>
      )}
      {children && (
        <Text userSelect="none" size={textSize} align="center" display="inline-flex">
          {children}
        </Text>
      )}
      {iconRight && (
        <Box
          as="span"
          display="inline-flex"
          disabled={disabled}
          cssClass={styles.iconVariants({
            size,
            emphasis,
            kind,
          })}
        >
          {iconRight}
        </Box>
      )}
    </>
  );
};
